import { FC, createContext, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  NumberParam,
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import {
  calendarEventsSelectorCreator,
  clearDataByKey,
  dispatchGetEvents,
  eventsSelector as allEventsSelector,
} from "Bands/state";
import { IEvent } from "Bands/types";

import { getEventBandId } from "Bands/utils";
import { LocalStorageService } from "shared/services";
import { SessionStorageService } from "shared/services/SessionStorageService";
import { IDataKeys, IDate, ILocalStorageKey } from "shared/types";
import { dateToIso, getMonthInfo } from "shared/utils";
import { useBandContext } from ".";

const today = new Date();

export enum ViewKeys {
  list = "list",
  calendar = "calendar",
}

interface CalendarContextState {
  today: Date;
  view: string;
  setView: (view: string) => void;
  setDate: (date: IDate) => void;
  date: IDate;
  start?: string;
  stop?: string;
  events: IEvent[];
  allEvents: IEvent[];
  refetchEvents: () => void;
}

const initialState: CalendarContextState = {
  today,
  view: ViewKeys.calendar,
  setView: () => {},
  setDate: () => {},
  date: {
    month: today.getMonth(),
    year: today.getFullYear(),
  },
  events: [],
  allEvents: [],
  start: undefined,
  stop: undefined,
  refetchEvents: () => {},
};

const CalendarContext = createContext<CalendarContextState>(initialState);

export const CalendarProvider: FC = ({ children }) => {
  const { bandId, userBands } = useBandContext();

  // get calendar view from URL
  const [view, setView] = useQueryParam(
    "view",
    withDefault(
      StringParam,
      // default: use calendar view type from localStorage, fallback to calendar
      (LocalStorageService.getItem(ILocalStorageKey.calendarView) as string) || ViewKeys.calendar
    )
  );

  // get date from URL
  const [date, setDate] = useQueryParams({
    month: withDefault(NumberParam, SessionStorageService.getItem("month") ?? today.getMonth()),
    year: withDefault(NumberParam, SessionStorageService.getItem("year") ?? today.getFullYear()),
  });

  // start/stop of events API call
  const start = useMemo(() => dateToIso({ ...date, day: 1 }), [date]);
  const stop = useMemo(() => {
    const { daysInMonth } = getMonthInfo(date);
    return dateToIso({ ...date, day: daysInMonth });
  }, [date]);

  const eventsSelector = useMemo(
    () => calendarEventsSelectorCreator(bandId ?? undefined, start, stop),
    [bandId, start, stop]
  );

  const basicEvents = useSelector(eventsSelector);
  const allEvents = useSelector(allEventsSelector);

  // enrich events with band info
  const events = useMemo(() => {
    const enrichedEvents = basicEvents.map(event => ({
      ...event,
      bandName: userBands?.find(band => {
        if (!event) return undefined;

        const bandId = getEventBandId(event);

        return band.id === bandId;
      })?.title,
    }));

    return enrichedEvents;
  }, [basicEvents, userBands]);

  // save calendar view type in localStorage
  useEffect(() => {
    LocalStorageService.setItem(ILocalStorageKey.calendarView, view);
  }, [view]);

  // refetch events and update state when needed
  const dispatch = useDispatch();
  const refetchEvents = () => {
    dispatch(clearDataByKey(IDataKeys.calendar));
    dispatchGetEvents(dispatch, bandId ?? undefined, start, stop);
  };

  return (
    <CalendarContext.Provider
      value={{
        today,
        view,
        date,
        events,
        allEvents,
        start,
        stop,
        setView,
        setDate,
        refetchEvents,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendarContext = () => useContext(CalendarContext);
